import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '@utils/cn';

/**
 * Badge variants using class-variance-authority
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        success: 'border-transparent bg-success text-success-foreground hover:bg-success/80',
        warning: 'border-transparent bg-warning text-warning-foreground hover:bg-warning/80',
        error:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        info: 'border-transparent bg-info text-info-foreground hover:bg-info/80',
        outline: 'text-foreground border-border hover:bg-accent',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-0.5 text-sm',
        lg: 'px-3 py-1 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  /**
   * Make badge dismissible with a close button
   */
  dismissible?: boolean;
  /**
   * Callback when badge is dismissed
   */
  onDismiss?: () => void;
  /**
   * Icon to display before the badge text
   */
  icon?: React.ReactNode;
  /**
   * Show a dot indicator before the text
   */
  dot?: boolean;
}

/**
 * Badge Component
 *
 * A versatile badge component for displaying status, labels, counts, and categories.
 * Supports multiple severity variants and sizes.
 *
 * @example
 * // Basic badges
 * <Badge>Default</Badge>
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Critical</Badge>
 * <Badge variant="warning">Pending</Badge>
 *
 * @example
 * // Different sizes
 * <Badge size="sm">Small</Badge>
 * <Badge size="md">Medium</Badge>
 * <Badge size="lg">Large</Badge>
 *
 * @example
 * // Badge with icon
 * <Badge icon={<CheckIcon className="h-3 w-3" />} variant="success">
 *   Verified
 * </Badge>
 *
 * @example
 * // Badge with dot indicator
 * <Badge dot variant="success">Online</Badge>
 *
 * @example
 * // Dismissible badge
 * <Badge dismissible onDismiss={() => logger.log('dismissed')}>
 *   Dismissible Tag
 * </Badge>
 *
 * @example
 * // Medical use cases
 * <Badge variant="error">Allergy: Penicillin</Badge>
 * <Badge variant="warning">Elevated Blood Pressure</Badge>
 * <Badge variant="info">Blood Type: O+</Badge>
 * <Badge variant="success">Vaccinated</Badge>
 */
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, dismissible, onDismiss, icon, dot, children, ...props }, ref) => {
    const [dismissed, setDismissed] = React.useState(false);

    const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setDismissed(true);
      onDismiss?.();
    };

    if (dismissed) return null;

    return (
      <div ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props}>
        {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
        {icon && <span className="inline-flex shrink-0">{icon}</span>}
        {children}
        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            className="ml-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-background/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }
);
Badge.displayName = 'Badge';

/**
 * Status Badge Component
 *
 * A pre-configured badge for displaying status with appropriate colors.
 */
export type StatusType = 'active' | 'inactive' | 'pending' | 'completed' | 'failed' | 'cancelled';

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: StatusType;
}

const statusVariantMap: Record<StatusType, BadgeProps['variant']> = {
  active: 'success',
  inactive: 'secondary',
  pending: 'warning',
  completed: 'success',
  failed: 'error',
  cancelled: 'outline',
};

const statusLabelMap: Record<StatusType, string> = {
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/**
 * StatusBadge Component
 *
 * A specialized badge for displaying status with predefined styles.
 *
 * @example
 * <StatusBadge status="active" />
 * <StatusBadge status="pending" />
 * <StatusBadge status="failed" />
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({ status, children, ...props }) => {
  return (
    <Badge variant={statusVariantMap[status]} dot {...props}>
      {children || statusLabelMap[status]}
    </Badge>
  );
};

/**
 * Count Badge Component
 *
 * A badge for displaying numerical counts or notifications.
 */
export interface CountBadgeProps extends Omit<BadgeProps, 'children'> {
  count: number;
  /**
   * Maximum count to display before showing "99+"
   */
  max?: number;
  /**
   * Show badge even when count is 0
   */
  showZero?: boolean;
}

/**
 * CountBadge Component
 *
 * A specialized badge for displaying counts and notifications.
 *
 * @example
 * <CountBadge count={5} />
 * <CountBadge count={150} max={99} />
 * <CountBadge count={0} showZero />
 */
const CountBadge: React.FC<CountBadgeProps> = ({ count, max = 99, showZero = false, ...props }) => {
  if (count === 0 && !showZero) return null;

  const displayCount = count > max ? `${max}+` : count;

  return (
    <Badge size="sm" {...props}>
      {displayCount}
    </Badge>
  );
};

export { Badge, badgeVariants, StatusBadge, CountBadge };
